import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Loader2, Sparkles } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", zip_code: "", password: "",
  });
  const [agreed, setAgreed] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [featureMe, setFeatureMe] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the Community Guidelines to continue");
      return;
    }
    if (!agreedTerms) {
      toast.error("Please confirm you are 18 or older and agree to the Terms and Privacy Policy");
      return;
    }
    setLoading(true);
    try {
      await register({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        zip_code: form.zip_code.trim(),
        password: form.password,
        agreed_to_guidelines: true,
        agreed_to_terms: true,
        newsletter_opt_in: newsletter,
        feature_in_newsletter: featureMe,
      });
      toast.success("Welcome to the community!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2] flex flex-col lg:flex-row">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjBjb21tdW5pdHklMjBoZWxwaW5nfGVufDB8fHx8MTc4NzE4NDUyMnww&ixlib=rb-4.1.0&q=85"
          alt="Community"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1B2A4A]/70 via-[#1B2A4A]/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-[#E8621A] flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <p className="font-heading font-bold text-2xl">Surviving Together</p>
          </div>
          <h1 className="font-heading font-bold text-4xl leading-tight mb-3">
            Join a community of neighbors saving smarter.
          </h1>
          <p className="text-white/85 text-base max-w-md">
            Free forever. Share what works, learn from others, and support each other through the everyday.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-2xl bg-[#E8621A] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-heading font-bold text-lg text-[#1B2A4A]">Surviving Together</p>
              <p className="text-xs text-[#1B2A4A]/60">Thriving Together</p>
            </div>
          </div>

          <h2 className="font-heading text-3xl font-bold text-[#1B2A4A] mb-2">Create your account</h2>
          <p className="text-[#1B2A4A]/60 mb-8">Takes less than a minute.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-[#1B2A4A] font-semibold text-sm">First name</Label>
                <Input id="first_name" data-testid="register-firstname" required value={form.first_name} onChange={set("first_name")}
                  className="h-12 rounded-xl border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-[#1B2A4A] font-semibold text-sm">Last name</Label>
                <Input id="last_name" data-testid="register-lastname" required value={form.last_name} onChange={set("last_name")}
                  className="h-12 rounded-xl border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1B2A4A] font-semibold text-sm">Email</Label>
              <Input id="email" data-testid="register-email" type="email" required value={form.email} onChange={set("email")}
                placeholder="you@example.com"
                className="h-12 rounded-xl border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zip" className="text-[#1B2A4A] font-semibold text-sm">Zip code</Label>
              <Input id="zip" data-testid="register-zip" required value={form.zip_code} onChange={set("zip_code")}
                placeholder="44113"
                className="h-12 rounded-xl border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1B2A4A] font-semibold text-sm">Password</Label>
              <Input id="password" data-testid="register-password" type="password" required value={form.password} onChange={set("password")}
                placeholder="At least 6 characters"
                className="h-12 rounded-xl border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50" />
            </div>

            <div className="rounded-2xl bg-[#F7F5F2] border border-[#1B2A4A]/8 p-4 flex items-start gap-3">
              <Checkbox
                id="agree"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(!!v)}
                data-testid="register-agree-checkbox"
                className="mt-0.5 data-[state=checked]:bg-[#E8621A] data-[state=checked]:border-[#E8621A]"
              />
              <Label htmlFor="agree" className="text-sm text-[#1B2A4A]/85 leading-relaxed cursor-pointer font-normal">
                I agree to the{" "}
                <Link
                  to="/guidelines"
                  target="_blank"
                  data-testid="register-guidelines-link"
                  className="text-[#E8621A] font-semibold hover:underline"
                >
                  Surviving Together Community Guidelines
                </Link>
                . I'll be kind, keep it practical, and help others feel welcome.
              </Label>
            </div>

            <div className="rounded-2xl bg-[#F7F5F2] border border-[#1B2A4A]/8 p-4 flex items-start gap-3">
              <Checkbox
                id="agree-terms"
                checked={agreedTerms}
                onCheckedChange={(v) => setAgreedTerms(!!v)}
                data-testid="register-terms-checkbox"
                className="mt-0.5 data-[state=checked]:bg-[#E8621A] data-[state=checked]:border-[#E8621A]"
              />
              <Label htmlFor="agree-terms" className="text-sm text-[#1B2A4A]/85 leading-relaxed cursor-pointer font-normal">
                I am 18 or older and agree to the{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  data-testid="register-terms-link"
                  className="text-[#E8621A] font-semibold hover:underline"
                >
                  Terms
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  data-testid="register-privacy-link"
                  className="text-[#E8621A] font-semibold hover:underline"
                >
                  Privacy Policy
                </Link>
                .
              </Label>
            </div>

            <div className="flex items-start gap-3 px-1">
              <Checkbox
                id="newsletter"
                checked={newsletter}
                onCheckedChange={(v) => setNewsletter(!!v)}
                data-testid="register-newsletter-checkbox"
                className="mt-0.5 data-[state=checked]:bg-[#2A9D8F] data-[state=checked]:border-[#2A9D8F]"
              />
              <Label htmlFor="newsletter" className="text-sm text-[#1B2A4A]/85 leading-relaxed cursor-pointer font-normal">
                Send me the Surviving Together newsletter.
              </Label>
            </div>

            <div className="flex items-start gap-3 px-1">
              <Checkbox
                id="feature-me"
                checked={featureMe}
                onCheckedChange={(v) => setFeatureMe(!!v)}
                data-testid="register-feature-checkbox"
                className="mt-0.5 data-[state=checked]:bg-[#2A9D8F] data-[state=checked]:border-[#2A9D8F]"
              />
              <Label htmlFor="feature-me" className="text-sm text-[#1B2A4A]/85 leading-relaxed cursor-pointer font-normal">
                OK to feature my first name in the newsletter.
              </Label>
            </div>

            <Button type="submit" data-testid="register-submit" disabled={loading || !agreed || !agreedTerms}
              className="w-full h-12 rounded-full bg-[#E8621A] hover:bg-[#D05615] text-white font-semibold text-base transition-colors duration-200 disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Join the community"}
            </Button>

            <p className="text-center text-sm text-[#1B2A4A]/70">
              Already have an account?{" "}
              <Link data-testid="link-login" to="/login" className="text-[#E8621A] font-semibold hover:underline">
                Sign in
              </Link>
            </p>

            <p className="text-center text-xs text-[#1B2A4A]/50 pt-2">
              <Link to="/privacy" data-testid="register-footer-privacy" className="hover:text-[#E8621A] font-semibold">
                Privacy Policy
              </Link>
              <span className="mx-2">·</span>
              <Link to="/terms" data-testid="register-footer-terms" className="hover:text-[#E8621A] font-semibold">
                Terms of Service
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
